function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable class-methods-use-this */
import { getOrThrow } from '@deephaven/utils';
import CellRenderer from "./CellRenderer.js";
import { isExpandableGridModel } from "./ExpandableGridModel.js";
import { isDataBarGridModel } from "./DataBarGridModel.js";
import GridColorUtils from "./GridColorUtils.js";
import GridUtils from "./GridUtils.js";
import memoizeClear from "./memoizeClear.js";
class DataBarCellRenderer extends CellRenderer {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getCachedWidth", memoizeClear((context, text) => context.measureText(text).width, {
      max: 10000
    }));
    /**
     * Returns the width of the widest value in pixels
     */
    _defineProperty(this, "getCachedWidestValueForColumn", memoizeClear((context, visibleRows, modelRows, model, column) => {
      var widestValue = 0;
      for (var i = 0; i < visibleRows.length; i += 1) {
        var row = visibleRows[i];
        var modelRow = getOrThrow(modelRows, row);
        var text = model.textForCell(column, modelRow);
        widestValue = Math.max(widestValue, this.getCachedWidth(context, text));
      }
      return widestValue;
    }, {
      max: 1000
    }));
  }
  drawCellContent(context, state, column, row) {
    var metrics = state.metrics,
      model = state.model,
      theme = state.theme;
    if (!isDataBarGridModel(model)) {
      return;
    }
    var modelColumns = metrics.modelColumns,
      modelRows = metrics.modelRows,
      allRowHeights = metrics.allRowHeights,
      allRowYs = metrics.allRowYs,
      firstColumn = metrics.firstColumn,
      fontWidthsLower = metrics.fontWidthsLower,
      fontWidthsUpper = metrics.fontWidthsUpper;
    var isFirstColumn = column === firstColumn;
    var rowHeight = getOrThrow(allRowHeights, row);
    var modelRow = getOrThrow(modelRows, row);
    var modelColumn = getOrThrow(modelColumns, column);
    var rowY = getOrThrow(allRowYs, row);
    var textAlign = model.textAlignForCell(modelColumn, modelRow);
    var text = model.textForCell(modelColumn, modelRow);
    var _GridUtils$getTextRen = GridUtils.getTextRenderMetrics(state, column, row),
      textX = _GridUtils$getTextRen.x,
      textY = _GridUtils$getTextRen.y,
      textWidth = _GridUtils$getTextRen.width;
    var fontWidthLower = fontWidthsLower.get(context.font);
    var fontWidthUpper = fontWidthsUpper.get(context.font);
    var truncationChar = model.truncationCharForCell(modelColumn, modelRow);
    var truncatedText = this.getCachedTruncatedString(context, text, textWidth, fontWidthLower, fontWidthUpper, truncationChar);
    var _model$dataBarOptions = model.dataBarOptionsForCell(modelColumn, modelRow, theme),
      columnMin = _model$dataBarOptions.columnMin,
      columnMax = _model$dataBarOptions.columnMax,
      axis = _model$dataBarOptions.axis,
      dataBarColor = _model$dataBarOptions.color,
      textColor = _model$dataBarOptions.textColor,
      valuePlacement = _model$dataBarOptions.valuePlacement,
      opacity = _model$dataBarOptions.opacity,
      markers = _model$dataBarOptions.markers,
      direction = _model$dataBarOptions.direction,
      value = _model$dataBarOptions.value;
    var hasGradient = Array.isArray(dataBarColor) && dataBarColor.length > 1;
    if (columnMin == null || columnMax == null) {
      return;
    }
    var _this$getDataBarRende = this.getDataBarRenderMetrics(context, state, column, row),
      maxWidth = _this$getDataBarRende.maxWidth,
      dataBarX = _this$getDataBarRende.x,
      dataBarY = _this$getDataBarRende.y,
      zeroPosition = _this$getDataBarRende.zeroPosition,
      leftmostPosition = _this$getDataBarRende.leftmostPosition,
      markerXs = _this$getDataBarRende.markerXs,
      totalValueRange = _this$getDataBarRende.totalValueRange,
      dataBarWidth = _this$getDataBarRende.dataBarWidth;
    context.save();
    context.textAlign = textAlign;
    context.fillStyle = textColor;
    context.textBaseline = 'middle';
    context.font = theme.font;
    if (valuePlacement !== 'hide') {
      context.fillText(truncatedText, textX, textY);
    }
    var hasRowDividers = theme.gridRowColor != null;
    var yOffset = hasRowDividers ? 2 : 1;
    context.save();
    context.beginPath();
    context.roundRect(dataBarX, rowY + yOffset,
    // yOffset includes 1px for top padding
    dataBarWidth, rowHeight - 1 - yOffset,
    // 1px for bottom padding
    1);
    context.clip();
    context.globalAlpha = opacity;

    // Draw bar
    if (hasGradient) {
      // Draw gradient bar

      var gradientWidth = 0;
      var gradientX = 0;
      context.save();

      // Translate the context so its origin is at the start of the gradient
      // and increasing x value moves towards the end of the gradient.
      // For RTL, scale x by -1 to flip across the x-axis
      if (value < 0) {
        if (direction === 'LTR') {
          gradientWidth = Math.round(Math.abs(columnMin) / totalValueRange * maxWidth);
          gradientX = Math.round(leftmostPosition);
          context.translate(gradientX, 0);
        } else if (direction === 'RTL') {
          gradientWidth = Math.round(maxWidth - Math.abs(columnMax) / totalValueRange * maxWidth);
          gradientX = Math.round(zeroPosition);
          context.translate(gradientX + gradientWidth, 0);
          context.scale(-1, 1);
        }
      } else if (direction === 'LTR') {
        // Value is greater than or equal to 0
        gradientWidth = Math.round(maxWidth - Math.abs(columnMin) / totalValueRange * maxWidth) - 1;
        gradientX = Math.round(zeroPosition);
        context.translate(gradientX, 0);
      } else if (direction === 'RTL') {
        // Value is greater than or equal to 0
        gradientWidth = Math.round(Math.abs(columnMax) / totalValueRange * maxWidth);
        gradientX = Math.round(leftmostPosition);
        context.translate(gradientX + gradientWidth, 0);
        context.scale(-1, 1);
      }
      var gradient = DataBarCellRenderer.getGradient(gradientWidth, dataBarColor);
      context.fillStyle = gradient;
      context.fillRect(0, dataBarY, gradientWidth, rowHeight);
      context.restore(); // Restore gradient translate/scale
    } else {
      // Draw normal bar
      var barColor = Array.isArray(dataBarColor) ? dataBarColor[0] : dataBarColor;
      context.fillStyle = barColor;
      context.beginPath();
      context.roundRect(dataBarX, dataBarY, dataBarWidth, rowHeight, 1);
      context.fill();
    }

    // Draw markers
    if (maxWidth > 0) {
      markerXs.forEach((markerX, index) => {
        context.fillStyle = markers[index].color;
        context.fillRect(markerX, dataBarY, 1, rowHeight);
      });
    }

    // restore clip
    context.restore();
    var shouldRenderDashedLine = !(axis === 'directional' && (valuePlacement === 'beside' && textAlign === 'right' && direction === 'LTR' || valuePlacement === 'beside' && textAlign === 'left' && direction === 'RTL' || valuePlacement !== 'beside'));

    // Draw dashed line
    if (shouldRenderDashedLine) {
      context.strokeStyle = theme.zeroLineColor;
      context.beginPath();
      context.setLineDash([2, 1]);
      context.moveTo(zeroPosition, rowY);
      context.lineTo(zeroPosition, rowY + rowHeight);
      context.stroke();
    }
    context.restore();

    // Draw tree marker
    if (isFirstColumn && isExpandableGridModel(model) && model.hasExpandableRows) {
      this.drawCellRowTreeMarker(context, state, row);
    }
  }
  getDataBarRenderMetrics(context, state, column, row) {
    var metrics = state.metrics,
      model = state.model,
      theme = state.theme;
    if (!isDataBarGridModel(model)) {
      throw new Error('Grid model is not a data bar grid model');
    }
    var firstColumn = metrics.firstColumn,
      allColumnXs = metrics.allColumnXs,
      allColumnWidths = metrics.allColumnWidths,
      allRowYs = metrics.allRowYs,
      modelColumns = metrics.modelColumns,
      modelRows = metrics.modelRows,
      visibleRows = metrics.visibleRows;
    var cellHorizontalPadding = theme.cellHorizontalPadding,
      treeDepthIndent = theme.treeDepthIndent,
      treeHorizontalPadding = theme.treeHorizontalPadding;
    var modelColumn = getOrThrow(modelColumns, column);
    var modelRow = getOrThrow(modelRows, row);
    var x = getOrThrow(allColumnXs, column);
    var y = getOrThrow(allRowYs, row);
    var columnWidth = getOrThrow(allColumnWidths, column);
    var isFirstColumn = column === firstColumn;
    var treeIndent = 0;
    if (isExpandableGridModel(model) && model.hasExpandableRows && isFirstColumn) {
      treeIndent = treeDepthIndent * (model.depthForRow(row) + 1) + treeHorizontalPadding;
    }
    var textAlign = model.textAlignForCell(modelColumn, modelRow);
    var _model$dataBarOptions2 = model.dataBarOptionsForCell(modelColumn, modelRow, theme),
      columnMin = _model$dataBarOptions2.columnMin,
      columnMax = _model$dataBarOptions2.columnMax,
      axis = _model$dataBarOptions2.axis,
      valuePlacement = _model$dataBarOptions2.valuePlacement,
      markers = _model$dataBarOptions2.markers,
      direction = _model$dataBarOptions2.direction,
      value = _model$dataBarOptions2.value;
    var longestValueWidth = this.getCachedWidestValueForColumn(context, visibleRows, modelRows, model, modelColumn);
    var leftPadding = 2;
    var rightPadding = valuePlacement === 'beside' && textAlign === 'right' ? 2 : 1;

    // The value of the total range (e.g. max - column)
    var totalValueRange = columnMax - columnMin;
    // If min and max are both positive or min and max are equal, the max length is columnMax
    if (columnMax >= 0 && columnMin >= 0 || columnMin === columnMax) {
      totalValueRange = columnMax;
    } else if (columnMax <= 0 && columnMin <= 0) {
      // If min and max are both negative, the max length is the absolute value of columnMin
      totalValueRange = Math.abs(columnMin);
    }
    var maxWidth = columnWidth - treeIndent - rightPadding - leftPadding;
    if (valuePlacement === 'beside') {
      maxWidth = maxWidth - cellHorizontalPadding - longestValueWidth;
    }
    if (maxWidth < 0) {
      maxWidth = 0;
    }
    var columnLongest = Math.max(Math.abs(columnMin), Math.abs(columnMax));
    // If axis is proportional, totalValueRange is proportional to maxWidth
    var dataBarWidth = Math.abs(value) / totalValueRange * maxWidth;
    if (maxWidth === 0) {
      dataBarWidth = 0;
    } else if (axis === 'middle') {
      // The longest bar is proportional to half of the maxWidth
      dataBarWidth = Math.abs(value) / columnLongest * (maxWidth / 2);
    } else if (axis === 'directional') {
      // The longest bar is proportional to the maxWidth
      dataBarWidth = Math.abs(value) / columnLongest * maxWidth;
    }

    // Default: proportional, beside, LTR, right text align
    // All positions are assuming the left side is 0 and the right side is maxWidth
    var zeroPosition = columnMin >= 0 ? 0 : Math.abs(columnMin) / totalValueRange * maxWidth;
    var dataBarX = value >= 0 ? zeroPosition : zeroPosition - Math.abs(value) / totalValueRange * maxWidth;
    var markerXs = markers.map(marker => {
      var markerValue = marker.value;
      var offset = Math.abs(markerValue) / totalValueRange * maxWidth;
      return markerValue >= 0 ? zeroPosition + offset : zeroPosition - offset;
    });
    var leftmostPosition = valuePlacement === 'beside' && textAlign === 'left' ? cellHorizontalPadding + longestValueWidth + leftPadding : leftPadding;
    var rightmostPosition = valuePlacement === 'beside' && textAlign === 'right' ? columnWidth - cellHorizontalPadding - longestValueWidth - rightPadding : rightPadding;

    // Proportional, RTL
    if (direction === 'RTL') {
      zeroPosition = columnMin >= 0 ? columnWidth : columnWidth - Math.abs(columnMin) / totalValueRange * maxWidth;
      dataBarX = value >= 0 ? zeroPosition - value / totalValueRange * maxWidth : zeroPosition;
      markerXs = markers.map(marker => {
        var markerValue = marker.value;
        return markerValue >= 0 ? zeroPosition - Math.abs(markerValue) / totalValueRange * maxWidth : zeroPosition + Math.abs(markerValue) / totalValueRange * maxWidth;
      });
    }
    if (axis === 'middle') {
      zeroPosition = maxWidth / 2;
      if (direction === 'LTR') {
        // Middle, LTR
        dataBarX = value >= 0 ? zeroPosition : zeroPosition - Math.abs(value) / columnLongest * (maxWidth / 2);
        markerXs = markers.map(marker => {
          var markerValue = marker.value;
          return markerValue >= 0 ? zeroPosition + Math.abs(markerValue) / columnLongest * (maxWidth / 2) : zeroPosition - Math.abs(markerValue) / columnLongest * (maxWidth / 2);
        });
      } else if (direction === 'RTL') {
        // Middle, RTL
        dataBarX = value <= 0 ? zeroPosition : zeroPosition - Math.abs(value) / columnLongest * (maxWidth / 2);
        markerXs = markers.map(marker => {
          var markerValue = marker.value;
          return markerValue <= 0 ? zeroPosition + Math.abs(markerValue) / columnLongest * (maxWidth / 2) : zeroPosition - Math.abs(markerValue) / columnLongest * (maxWidth / 2);
        });
      }
    } else if (axis === 'directional') {
      if (direction === 'LTR') {
        // Directional, LTR
        zeroPosition = 0;
        dataBarX = zeroPosition;
        markerXs = markers.map(marker => {
          var markerValue = marker.value;
          return zeroPosition + Math.abs(markerValue) / columnLongest * maxWidth;
        });
      } else if (direction === 'RTL') {
        // Directional, RTL
        zeroPosition = columnWidth;
        dataBarX = zeroPosition - Math.abs(value) / columnLongest * maxWidth;
        markerXs = markers.map(marker => {
          var markerValue = marker.value;
          return zeroPosition - Math.abs(markerValue) / columnLongest * maxWidth;
        });
      }
    }

    // Offset all values by the actual x value and padding
    if (direction === 'LTR') {
      zeroPosition += x + leftPadding + treeIndent;
      dataBarX += x + leftPadding + treeIndent;
      markerXs = markerXs.map(markerX => markerX + x + leftPadding + treeIndent);
      if (valuePlacement === 'beside' && textAlign === 'left') {
        zeroPosition += longestValueWidth + cellHorizontalPadding;
        dataBarX += longestValueWidth + cellHorizontalPadding;
        markerXs = markerXs.map(markerX => markerX + longestValueWidth + cellHorizontalPadding);
      }
    } else if (direction === 'RTL') {
      zeroPosition = zeroPosition + x - rightPadding;
      dataBarX = dataBarX + x - rightPadding;
      markerXs = markerXs.map(markerX => markerX + x - rightPadding);
      if (valuePlacement === 'beside' && textAlign === 'right') {
        zeroPosition = zeroPosition - cellHorizontalPadding - longestValueWidth;
        dataBarX = dataBarX - cellHorizontalPadding - longestValueWidth;
        markerXs = markerXs.map(markerX => markerX - cellHorizontalPadding - longestValueWidth);
      }
    }
    leftmostPosition += x + treeIndent;
    rightmostPosition += x;
    return {
      maxWidth,
      x: dataBarX,
      y,
      zeroPosition,
      leftmostPosition,
      rightmostPosition,
      totalValueRange,
      dataBarWidth,
      markerXs
    };
  }
}
_defineProperty(DataBarCellRenderer, "getGradient", memoizeClear((width, colors) => {
  var canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d');
  if (ctx == null) {
    throw new Error('Failed to create canvas context');
  }
  if (Number.isNaN(width)) {
    return ctx.createLinearGradient(0, 0, 0, 0);
  }
  var gradient = ctx.createLinearGradient(0, 0, width, 0);
  var oklabColors = colors.map(color => GridColorUtils.linearSRGBToOklab(GridColorUtils.hexToRgb(color)));
  for (var i = 0; i < width; i += 1) {
    var colorStop = i / width;
    var colorChangeInterval = 1 / (colors.length - 1);
    var leftColorIndex = Math.floor(colorStop / colorChangeInterval);
    var color = GridColorUtils.lerpColor(oklabColors[leftColorIndex], oklabColors[leftColorIndex + 1], colorStop % colorChangeInterval / colorChangeInterval);
    gradient.addColorStop(i / width, GridColorUtils.rgbToHex(GridColorUtils.OklabToLinearSRGB(color)));
  }
  return gradient;
}, {
  max: 1000,
  primitive: true // Stringify the arguments for memoization. Lets the color arrays be different arrays in memory, but still cache hit
}));
export default DataBarCellRenderer;
//# sourceMappingURL=DataBarCellRenderer.js.map